import { createStyles, keyframes } from "@mantine/core";

const brandColor = "rgb(42, 155, 73)";
const brandColorLight = "rgba(42, 155, 73, 0.08)";

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const slideUp = keyframes({
  from: { transform: "translateY(10px)", opacity: 0 },
  to: { transform: "translateY(0)", opacity: 1 },
});

export const useStyles = createStyles((theme) => ({
  container: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[9] : "#f6f7f8",
  },
  component: {
    flex: 1,
    padding: "24px",
    maxWidth: 1280,
    margin: "0 auto",
    overflow: "auto",
    animation: `${fadeIn} 0.4s ease`,
  },

  navHeader: {
    paddingBottom: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderBottom: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[2]}`,
  },
  link: {
    ...theme.fn.focusStyles(),
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    fontSize: 16,
    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.gray[7],
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    borderRadius: theme.radius.md,
    fontWeight: 500,
    margin: "4px 0",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
      color: theme.colorScheme === "dark" ? theme.white : brandColor,
      transform: "translateX(3px)",
    },
  },
  linkActive: {
    "&, &:hover": {
      backgroundColor: theme.colorScheme === "dark" ? `rgba(42, 155, 73, 0.2)` : brandColorLight,
      color: theme.colorScheme === "dark" ? theme.white : brandColor,
      borderRight: `3px solid ${brandColor}`,
    },
  },

  title: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: 28,
    letterSpacing: 0.5,
    color: theme.colorScheme === "dark" ? theme.white : theme.colors.gray[9],
    marginBottom: theme.spacing.md,
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    animation: `${slideUp} 0.4s ease`,
    "@media (max-width: 520px)": { fontSize: 24, textAlign: "center" },
  },

  card: {
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : "#e9ecef"}`,
    padding: theme.spacing.lg,
    transition: "border-color 0.2s ease, transform 0.2s ease",
    boxShadow: "none",
    "&:hover": {
      borderColor: brandColor,
      transform: "translateY(-2px)",
    },
  },
  panel: {
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : "#e9ecef"}`,
    padding: theme.spacing.xl,
  },

  button: {
    backgroundColor: brandColor,
    color: theme.white,
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    fontWeight: 500,
    transition: "all 0.2s ease",
    border: "none",
    "&:hover": { backgroundColor: "rgb(35, 135, 60)", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
  },
  buttonLight: {
    backgroundColor: "transparent",
    color: brandColor,
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    fontWeight: 500,
    transition: "all 0.2s ease",
    border: `1px solid rgba(42, 155, 73, 0.2)`,
    "&:hover": { backgroundColor: brandColorLight, transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
  },

  input: {
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : "#e9ecef"}`,
    padding: theme.spacing.sm,
    transition: "border-color 0.2s ease",
    "&:focus": { borderColor: brandColor },
  },
  searchInput: {
    ".mantine-TextInput-input": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
      border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : "#e9ecef"}`,
      borderRadius: theme.radius.lg,
      transition: "border-color 0.2s ease",
      boxShadow: "none",
      "&:focus": { borderColor: brandColor, boxShadow: "none" },
    },
    ".mantine-TextInput-icon": { color: brandColor },
  },

  dropzone: {
    borderRadius: theme.radius.lg,
    borderWidth: "1px",
    borderStyle: "dashed",
    padding: theme.spacing.xl,
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : "#f8f9fa",
    transition: "all 0.2s ease",
    textAlign: "center",
    "&:hover": { borderColor: brandColor, backgroundColor: brandColorLight },
  },
}));
